import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FaArrowRight, FaGraduationCap, FaImages } from "react-icons/fa";
import { FaYoutube, FaInstagram, FaFacebookF } from "react-icons/fa";
import { RiShieldCheckLine } from "react-icons/ri";

const StarDot = ({ style }) => (
  <div
    className="absolute rounded-full pointer-events-none star-twinkle"
    style={{
      background: "#fff",
      boxShadow: "0 0 5px rgba(255,255,255,0.9)",
      ...style,
    }}
  />
);

const GoldDot = ({ style }) => (
  <div
    className="absolute rounded-full pointer-events-none star-twinkle"
    style={{
      background: "#f4c842",
      boxShadow: "0 0 6px rgba(244,200,66,0.9)",
      animationDelay: "1s",
      ...style,
    }}
  />
);

const backgroundImages = [
  "/renamed_website_images/infrastructure-section-screenshot.jpg",
  "/renamed_website_images/campus-001.png",
  "/renamed_website_images/campus-070.jpeg",
  "/renamed_website_images/smart-classroom.jpg",
];

const Display = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex(
        (prevIndex) => (prevIndex + 1) % backgroundImages.length,
      );
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden flex items-center justify-center pt-20">
      {/* Smooth Cross-Fading Background Slideshow (no black flashes) */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        {backgroundImages.map((img, idx) => (
          <div
            key={idx}
            className="absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ease-in-out"
            style={{
              backgroundImage: `url('${img}')`,
              opacity: currentImageIndex === idx ? 1 : 0,
              transform: currentImageIndex === idx ? "scale(1.02)" : "scale(1)",
              transition: "opacity 1.5s ease-in-out, transform 7s ease-out",
            }}
          />
        ))}
      </div>

      {/* Professional Gradient Overlay */}
      <div
        className="absolute inset-0 pointer-events-none z-1"
        style={{
          background:
            "linear-gradient(180deg, rgba(2,8,16,0.78) 0%, rgba(2,8,16,0.62) 40%, rgba(2,8,16,0.85) 75%, rgba(2,8,16,0.98) 100%)",
        }}
      />

      {/* Subtle Star Particles */}
      <div className="absolute inset-0 pointer-events-none opacity-40 z-2">
        <StarDot style={{ top: "15%", left: "10%", width: "2px", height: "2px", animationDelay: "0s" }} />
        <StarDot style={{ top: "25%", right: "15%", width: "3px", height: "3px", animationDelay: "1s" }} />
        <StarDot style={{ bottom: "30%", left: "20%", width: "2px", height: "2px", animationDelay: "1.5s" }} />
        <StarDot style={{ top: "40%", left: "45%", width: "1px", height: "1px", animationDelay: "2s" }} />
        <GoldDot style={{ top: "10%", left: "30%", width: "2px", height: "2px", animationDelay: "0.5s" }} />
        <GoldDot style={{ bottom: "45%", right: "25%", width: "3px", height: "3px", animationDelay: "2.5s" }} />
      </div>

      {/* Floating Side Social Icons */}
      <div className="fixed right-3 sm:right-6 top-1/2 -translate-y-1/2 z-40 flex flex-col gap-3">
        <a
          href="https://www.instagram.com/galaxystarschool/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Instagram"
          className="group relative w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg"
          style={{
            background: "rgba(2,8,20,0.85)",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(244,200,66,0.3)",
            color: "#f4c842",
          }}
        >
          <FaInstagram className="text-base sm:text-lg group-hover:text-pink-400 transition-colors" />
          <span className="absolute right-12 px-2.5 py-1 rounded bg-[#020810]/95 text-white text-[10px] font-bold tracking-wider uppercase whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none border border-gold/30">
            Instagram
          </span>
        </a>

        <a
          href="https://www.facebook.com/p/Galaxy-Star-Public-School-Sekhwan-100057501486855/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Facebook"
          className="group relative w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg"
          style={{
            background: "rgba(2,8,20,0.85)",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(244,200,66,0.3)",
            color: "#f4c842",
          }}
        >
          <FaFacebookF className="text-sm sm:text-base group-hover:text-blue-400 transition-colors" />
          <span className="absolute right-12 px-2.5 py-1 rounded bg-[#020810]/95 text-white text-[10px] font-bold tracking-wider uppercase whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none border border-gold/30">
            Facebook
          </span>
        </a>

        <a
          href="https://www.youtube.com/@galaxystarschool"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="YouTube"
          className="group relative w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-lg"
          style={{
            background: "rgba(2,8,20,0.85)",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(244,200,66,0.3)",
            color: "#f4c842",
          }}
        >
          <FaYoutube className="text-base sm:text-lg group-hover:text-red-500 transition-colors" />
          <span className="absolute right-12 px-2.5 py-1 rounded bg-[#020810]/95 text-white text-[10px] font-bold tracking-wider uppercase whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none border border-gold/30">
            YouTube
          </span>
        </a>
      </div>

      {/* Hero content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="relative z-10 text-center px-6 max-w-4xl mx-auto w-full py-12"
      >
        <div className="relative">
          {/* Affiliation Highlight Badge */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-6 flex justify-center"
          >
            <div className="inline-flex items-center gap-2.5 px-5 py-2.5 rounded-full bg-gold/10 border border-gold/40 backdrop-blur-md shadow-[0_4px_25px_rgba(244,200,66,0.25)] text-white text-xs md:text-sm font-semibold tracking-wide">
              <span className="flex h-2.5 w-2.5 relative flex-shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-gold" />
              </span>
              <RiShieldCheckLine className="text-gold text-base" />
              <span className="text-gold font-bold uppercase tracking-wider">
                Affiliated to ICSE & ISC, PU-158
              </span>
              <span className="text-gray-200 font-medium hidden sm:inline">
                (Pre-Nursery to 12th)
              </span>
            </div>
          </motion.div>

          {/* Subtitle Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="mb-8 flex justify-center"
          >
            <span className="section-badge px-6 py-2 tracking-widest uppercase text-xs">
              <FaGraduationCap className="text-gold mr-2 inline-block" />
              Empowering Future Leaders
            </span>
          </motion.div>

          {/* School Name */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <h1
              className="font-black tracking-tight leading-none mb-3"
              style={{
                fontFamily: "Orbitron, sans-serif",
                fontSize: "clamp(2.5rem, 8vw, 5rem)",
                color: "#f4c842",
                filter: "drop-shadow(0 0 25px rgba(244,200,66,0.35))",
                textShadow: "0 4px 20px rgba(0,0,0,0.9)",
              }}
            >
              GALAXY STAR
            </h1>
            <h2
              className="font-black tracking-[0.25em] uppercase mb-6"
              style={{
                fontFamily: "Orbitron, sans-serif",
                fontSize: "clamp(1.2rem, 3.5vw, 2.5rem)",
                color: "#ffffff",
                textShadow: "0 2px 10px rgba(0,0,0,0.9)",
              }}
            >
              Public School
            </h2>
          </motion.div>

          {/* Divider */}
          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="cosmic-divider w-64 mx-auto mb-8 h-1"
          />

          {/* Subtext */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="mb-10 text-gray-300 max-w-2xl mx-auto font-medium leading-relaxed"
            style={{
              fontFamily: "Nunito, sans-serif",
              fontSize: "clamp(0.95rem, 1.8vw, 1.15rem)",
              textShadow: "0 2px 4px rgba(0,0,0,0.8)",
            }}
          >
            Fostering academic excellence, character building, and holistic development in Sekhwan. Where every child shines brightly.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 flex-wrap"
          >
            <Link
              to="/enroll"
              className="cosmic-btn-gold px-8 py-3.5 rounded-full font-extrabold text-xs uppercase tracking-[0.2em] shadow-lg flex items-center gap-2"
            >
              Admissions Open 2025-26 <FaArrowRight size={12} />
            </Link>
            <Link
              to="/facilities"
              className="px-8 py-3.5 rounded-full font-bold text-xs uppercase tracking-[0.2em] text-white border border-white/20 hover:border-gold/50 bg-white/5 hover:bg-gold/10 backdrop-blur-md transition-all duration-300"
            >
              Explore Facilities
            </Link>
            <Link
              to="/gallery"
              className="px-8 py-3.5 rounded-full font-bold text-xs uppercase tracking-[0.2em] text-gold border border-gold/40 hover:border-gold bg-gold/10 hover:bg-gold/20 backdrop-blur-md transition-all duration-300 flex items-center gap-2"
            >
              <FaImages size={13} /> Open Gallery
            </Link>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default Display;
